import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import { generateBerThongReceiptDataUrl } from '@huay/shared/ber-thong'
import {
  fetchBerThongReceipt,
  fetchMyBerThongPurchases,
  uploadBerThongReceipt,
  type BerThongPurchase,
} from '../../services/berThongService'
import { HistoryCollapseCard, type HistoryDetailRow } from './HistoryCollapseCard'
import '../points/berThongGame.css'

function formatDateTime(value?: string) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString('th-TH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function betTypeLabel(betType?: string) {
  return betType === 'top2' ? '2 ตัวบน' : '2 ตัวล่าง'
}

function statusTone(item: BerThongPurchase): 'success' | 'reject' | 'pending' | 'info' {
  if (item.status === 'refunded' || item.sheetStatus === 'cancelled') return 'reject'
  if (item.sheetStatus === 'settled') return 'success'
  if (item.sheetStatus === 'open') return 'pending'
  return 'info'
}

function statusLabel(item: BerThongPurchase) {
  if (item.status === 'refunded' || item.sheetStatus === 'cancelled') return 'ยกเลิก · คืนเงิน'
  if (item.sheetStatus === 'settled') return 'จ่ายผลแล้ว'
  if (item.sheetStatus === 'closed') return 'ปิดรับแล้ว'
  if (item.sheetStatus === 'open') return 'รอผล'
  return 'ซื้อแล้ว'
}

function buildRows(item: BerThongPurchase): HistoryDetailRow[] {
  const rows: HistoryDetailRow[] = [
    { label: 'รหัสแผ่น', value: item.sheetCode || '—' },
    { label: 'หวย', value: item.huayName || '—' },
    { label: 'งวด', value: item.drawDate || '—' },
    { label: 'ประเภท', value: betTypeLabel(item.betType) },
    { label: 'เบอร์ที่ซื้อ', value: item.numbers.join(', '), tone: 'positive' },
    { label: 'ราคา/เบอร์', value: `${Number(item.unitPrice || 0).toLocaleString('th-TH')} ฿` },
    { label: 'ยอดรวม', value: `${Number(item.totalAmount || 0).toLocaleString('th-TH')} ฿`, tone: 'positive' },
    { label: 'เวลาซื้อ', value: formatDateTime(item.createdAt) },
    { label: 'สถานะ', value: statusLabel(item) },
  ]
  if (item.winningNumber) {
    rows.push({ label: 'ผลรางวัล', value: item.winningNumber, tone: 'positive' })
  }
  return rows
}

const HistoryBerThongPage: React.FC = () => {
  const [items, setItems] = useState<BerThongPurchase[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const loadHistory = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await fetchMyBerThongPurchases()
      setItems(res.data || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'โหลดประวัติไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadHistory()
  }, [loadHistory])

  const stats = useMemo(() => {
    const paid = items.filter((i) => i.status !== 'refunded' && i.sheetStatus !== 'cancelled')
    const refunded = items.length - paid.length
    const spent = paid.reduce((sum, i) => sum + Number(i.totalAmount || 0), 0)
    return { total: items.length, paid: paid.length, refunded, spent }
  }, [items])

  const openReceipt = async (item: BerThongPurchase) => {
    try {
      let url = item.receiptUrl || ''
      if (!url) {
        const res = await fetchBerThongReceipt(item.id)
        url = res.data?.receiptUrl || ''
      }
      if (!url) {
        const local = generateBerThongReceiptDataUrl({
          sheetCode: item.sheetCode,
          sheetTitle: item.sheetTitle,
          huayName: item.huayName,
          drawDate: item.drawDate,
          betTypeLabel: betTypeLabel(item.betType),
          numbers: item.numbers,
          unitPrice: item.unitPrice,
          totalAmount: item.totalAmount,
          purchaseId: item.id,
          purchasedAt: item.createdAt,
        })
        if (!local) {
          await Swal.fire({
            icon: 'info',
            title: 'ยังไม่มีใบเสร็จในระบบ',
            text: 'การซื้อสำเร็จแล้ว แต่รูปใบเสร็จยังไม่ได้บันทึกบนเซิร์ฟเวอร์',
          })
          return
        }
        const result = await Swal.fire({
          title: 'ใบเสร็จเบอร์ทอง',
          imageUrl: local,
          imageAlt: 'ใบเสร็จ',
          width: 560,
          confirmButtonText: 'ปิด',
          showDenyButton: true,
          denyButtonText: 'บันทึกลงระบบ',
          showCancelButton: true,
          cancelButtonText: 'เปิดแท็บใหม่',
        })
        if (result.isDenied) {
          try {
            await uploadBerThongReceipt(item.id, local, 'image/png')
            await Swal.fire({ icon: 'success', title: 'บันทึกใบเสร็จแล้ว', confirmButtonText: 'ปิด' })
            await loadHistory()
          } catch (err) {
            await Swal.fire({
              icon: 'error',
              title: 'บันทึกไม่สำเร็จ',
              text: err instanceof Error ? err.message : 'ลองใหม่อีกครั้งภายหลัง',
            })
          }
          return
        }
        if (result.dismiss === Swal.DismissReason.cancel) {
          window.open(local, '_blank', 'noopener,noreferrer')
        }
        return
      }

      await Swal.fire({
        title: 'ใบเสร็จเบอร์ทอง',
        imageUrl: url,
        imageAlt: 'ใบเสร็จ',
        width: 560,
        confirmButtonText: 'ปิด',
        showDenyButton: true,
        denyButtonText: 'เปิดแท็บใหม่',
      }).then((result) => {
        if (result.isDenied) window.open(url, '_blank', 'noopener,noreferrer')
      })
    } catch (err) {
      await Swal.fire({
        icon: 'error',
        title: 'โหลดใบเสร็จไม่สำเร็จ',
        text: err instanceof Error ? err.message : 'ลองใหม่อีกครั้ง',
      })
    }
  }

  const handleCopyRef = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id)
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="htd-hist__stack">
      <h2 className="htd-hist__page-title">ประวัติเบอร์ทอง</h2>

      <div className="htd-hist__panel">
        <div className="htd-hist__summary">
          <div>
            <strong>รายการซื้อเบอร์เงินสด</strong>
            <span>แผ่น · เบอร์ · ยอด · สถานะ · ใบเสร็จ</span>
          </div>
          <div className="text-end">
            <Link to="/points/games/ber-thong" className="htd-hist__inline-link">
              ไปซื้อเบอร์
            </Link>
          </div>
        </div>
      </div>

      <div className="htd-hist__list">
        {loading && (
          <div className="htd-hist__loading">
            <h4>
              <i className="fas fa-spinner fa-spin me-2" />
              กำลังโหลดประวัติเบอร์ทอง...
            </h4>
          </div>
        )}

        {!loading && error && (
          <div className="htd-hist__shell">
            <h4>{error}</h4>
            <button type="button" className="htd-hist__retry-btn" onClick={() => void loadHistory()}>
              ลองโหลดอีกครั้ง
            </button>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="htd-hist__empty">
            <h4>ยังไม่มีประวัติการซื้อ</h4>
            <p>เมื่อซื้อเบอร์ทอง รายการจะแสดงที่นี่</p>
            <Link to="/points/games/ber-thong" className="htd-hist__inline-link">
              ไปซื้อเบอร์
            </Link>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            <div className="htd-hist__panel mb-2">
              <div className="htd-hist__summary htd-bt-hist__stats">
                <div>
                  <strong>{stats.total.toLocaleString('th-TH')} รายการ</strong>
                  <span>
                    ใช้งาน {stats.paid.toLocaleString('th-TH')}
                    {stats.refunded > 0 ? ` · คืนเงิน ${stats.refunded.toLocaleString('th-TH')}` : ''}
                  </span>
                </div>
                <div className="text-end">
                  <strong>{stats.spent.toLocaleString('th-TH')} ฿</strong>
                  <span>ยอดซื้อรวม (ไม่นับคืนเงิน)</span>
                </div>
              </div>
            </div>

            {items.map((item) => {
              const refunded = item.status === 'refunded' || item.sheetStatus === 'cancelled'
              const hasServerReceipt = Boolean(item.hasReceiptImage || item.receiptUrl)
              return (
                <div key={item.id} className={`htd-bt-hist__card-wrap${refunded ? ' is-refunded' : ''}`}>
                  <HistoryCollapseCard
                    id={item.id}
                    leftIcon={statusTone(item)}
                    refId={item.id}
                    onCopyRef={() => void handleCopyRef(item.id)}
                    rows={buildRows(item)}
                    actionLabel={hasServerReceipt ? 'ดูใบเสร็จ' : 'เปิดใบเสร็จ'}
                    onAction={() => void openReceipt(item)}
                    header={(
                      <div className="w-100 pe-md-4 htd-bt-hist__card-head">
                        <div className="htd-bt-hist__card-top">
                          <div className="htd-bt-hist__card-title">
                            {item.huayName || 'หวย'} · งวด {item.drawDate || '—'}
                          </div>
                          <div className="htd-bt-hist__card-amount">
                            {Number(item.totalAmount || 0).toLocaleString('th-TH')} ฿
                          </div>
                        </div>
                        <div className="htd-bt-hist__card-chips">
                          <span className="htd-bt-hist__chip">{betTypeLabel(item.betType)}</span>
                          <span className={`htd-bt-hist__chip htd-bt-hist__chip--status is-${statusTone(item)}`}>
                            {statusLabel(item)}
                          </span>
                          {!hasServerReceipt && (
                            <span className="htd-bt-hist__chip htd-bt-hist__chip--warn">ใบเสร็จในเครื่อง</span>
                          )}
                        </div>
                        <div className="htd-bt-hist__card-numbers">
                          เบอร์ {item.numbers.join(', ')}
                        </div>
                        <small className="htd-bt-hist__card-meta">
                          {formatDateTime(item.createdAt)}
                          {item.sheetCode ? ` · ${item.sheetCode}` : ''}
                        </small>
                        {item.sheetTitle ? (
                          <small className="htd-bt-hist__card-sheet">{item.sheetTitle}</small>
                        ) : null}
                      </div>
                    )}
                  />
                </div>
              )
            })}
          </>
        )}
      </div>
    </div>
  )
}

export default HistoryBerThongPage
